import React from "react";
import FooterOne from "../common/footer/FooterOne";
import HeaderOne from "../common/header/HeaderOne";
import SEO from "../common/SEO";
import BreadCrumbOne from "../elements/breadcrumb/BreadCrumbOne";
import ColorSwitcher from "../elements/switcher/ColorSwitcher";
import seoData from "../seoData/Design.json"
// import argumentTeam from "../assets/image/services/argumentTeam.svg"
// import liIcon from "../li-icon.svg"

const Design = () => {
  return (
    <>
      <SEO {...seoData} />
      <ColorSwitcher />
      <main className="main-wrapper StaffAugmentation">
        <HeaderOne />
        <BreadCrumbOne title="UI/UX Design" page="UI/UX Design" />

        <div className="section section-padding pb-0">
          <div className="container">
            <div className="row mb--40">
              <div className="col-lg-9">
                <h2 className="mb-3">Design that's built to be used, not just looked at</h2>
                <p>
                  Good design earns its keep by making something easier to use,
                  faster to trust, and quicker to remember — not by winning an
                  award nobody outside the industry cares about. Every project
                  we take on starts with the same question: what is the person
                  on the other end of this actually trying to get done? The
                  five services below are how we answer that, from a first
                  logo sketch through to a finished, produced video.
                </p>
              </div>
            </div>
            <section id="augmentation-card">
              <div className="row">
                <div className="col-11 col-lg-9 mx-auto ms-lg-0">
                  <div className="augmentation-card">
                    <h3 className="augmentation-title num3">
                      1. Logo &amp; Branding
                    </h3>
                    <p className="mb-0 mt-3">
                      A logo is the smallest, most-repeated piece of a brand —
                      which is exactly why it's worth getting right. We start
                      by understanding who the brand needs to speak to and what
                      it needs to signal (established or scrappy, playful or
                      serious) before a single concept gets sketched. From
                      there we build out a small system — logo, color palette,
                      typography, usage rules — so the mark holds up
                      consistently on a business card, a app icon, or a
                      billboard, not just the one mockup it was designed on.
                    </p>
                  </div>
                </div>
                <div className="col-11 col-lg-9 mt-4 mx-auto me-lg-4 ms-lg-auto">
                  <div className="augmentation-card">
                    <h3 className="augmentation-title num3">
                      2. Website Design
                    </h3>
                    <p className="mb-0 mt-3">
                      We design websites around what a visitor is trying to
                      accomplish, then let the visuals serve that — not the
                      other way around. That means clear navigation, a
                      deliberate visual hierarchy that guides the eye to what
                      matters on each page, and layouts that hold up cleanly
                      from a wide desktop monitor down to a phone screen. We
                      typically work in wireframes first, so the structure and
                      flow are validated before any color or imagery is
                      applied, which keeps later revisions cheap instead of
                      expensive.
                    </p>
                  </div>
                </div>
                <div className="col-11 col-lg-9 mt-4 mx-auto ms-lg-0">
                  <div className="augmentation-card">
                    <h3 className="augmentation-title num3">
                      3. Mobile App Design
                    </h3>
                    <p className="mb-0 mt-3">
                      Mobile design has its own constraints — a smaller screen,
                      thumbs instead of a mouse cursor, and platform-specific
                      conventions users already expect from every other app on
                      their phone. We design against those conventions rather
                      than around them, using established iOS and Android
                      interaction patterns where they genuinely help, and
                      departing from them only where a specific product need
                      justifies it. Every screen gets checked for how it
                      behaves at the actual sizes people use, not just a
                      designer's oversized monitor.
                    </p>
                  </div>
                </div>
                <div className="col-11 col-lg-9 mt-4 mx-auto me-lg-4 ms-lg-auto">
                  <div className="augmentation-card">
                    <h3 className="augmentation-title num3">
                      4. Graphic &amp; Print Design
                    </h3>
                    <p className="mb-0 mt-3">
                      Brochures, packaging, signage, trade-show banners —
                      print work has to get the message across in the few
                      seconds someone actually looks at it, with no scrolling
                      or clicking to fall back on. We design for the specific
                      medium from the start: proper bleed and color profiles
                      for print production, legible type at the size the piece
                      will actually be viewed at, and layouts proofed against
                      a real print sample before a full run goes to press.
                    </p>
                  </div>
                </div>
                <div className="col-11 col-lg-9 mt-4 mx-auto ms-lg-0">
                  <div className="augmentation-card">
                    <h3 className="augmentation-title num3">
                      5. Video Production
                    </h3>
                    <p className="mb-0 mt-3">
                      From a short product explainer to a full brand video, we
                      handle the process end to end — scripting, storyboarding,
                      filming or motion graphics, and editing. The brief we
                      write down before production starts is short and
                      specific: who's watching this, where will they see it,
                      and what should they do right after. That brief is what
                      keeps a video from becoming a beautiful piece nobody
                      quite knows what to do with once it's finished.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <div className="row mt--60">
              <div className="col-lg-9">
                <h2 className="mb-3">How a design engagement actually runs</h2>
                <p>
                  Design work goes wrong most often when feedback only arrives
                  at the end, after a lot of effort has already gone into one
                  direction. We structure engagements to avoid that.
                </p>
                <h3 className="mt--30 mb-2">1. Discovery</h3>
                <p>
                  A short set of questions and, where useful, a call — what's
                  the goal, who's the audience, what's already been tried.
                  This is also where we flag if a request needs research
                  (competitor review, a quick user survey) before design work
                  should start at all.
                </p>
                <h3 className="mt--30 mb-2">2. Concepts</h3>
                <p>
                  We present a small number of genuinely different directions,
                  not ten variations on the same idea — it's a harder edit for
                  us, but a much easier decision for you. Feedback at this
                  stage is cheap to act on, since nothing has been built out
                  in detail yet.
                </p>
                <h3 className="mt--30 mb-2">3. Refinement</h3>
                <p>
                  Once a direction is picked, we develop it into full detail —
                  every screen, every asset, every size variant it needs to
                  work across. Revisions here are scoped and tracked, so
                  nothing gets lost between rounds of feedback.
                </p>
                <h3 className="mt--30 mb-2">4. Delivery</h3>
                <p>
                  Final files, organized and named sensibly, in whatever
                  formats the next step needs — production-ready assets for
                  print, a structured handoff file for developers, or both.
                  We don't consider a project finished until whoever receives
                  the files can actually use them without coming back to ask
                  what something is.
                </p>
              </div>
            </div>
          </div>
          <ul className="list-unstyled shape-group-12">
            <li className="shape shape-1">
              <img
                src={process.env.PUBLIC_URL + "/images/others/bubble-2.png"}
                alt="Bubble"
              />
            </li>
            <li className="shape shape-2">
              <img
                src={process.env.PUBLIC_URL + "/images/others/bubble-1.png"}
                alt="Bubble"
              />
            </li>
            <li className="shape shape-3">
              <img
                src={process.env.PUBLIC_URL + "/images/others/circle-3.png"}
                alt="Circle"
              />
            </li>
          </ul>
        </div>

        <FooterOne parentClass="pt--150 pt_lg--100 pt_md--80 pt_sm--60" />
      </main>
    </>
  );
};

export default Design;
